import React from 'react';

interface GrowthData {
  weight_kg: number | null;
  height_cm: number | null;
  head_cm: number | null;
  measurement_method: string;
  weight_percentile: number | null;
  height_percentile: number | null;
  head_percentile: number | null;
  bmi: number | null;
  bmi_percentile: number | null;
}

interface Props {
  growth: GrowthData;
  setPediatricField: (section: string, field: string, value: any) => void;
  patientBirthDate: string | null;
}

function getPercentileColor(p: number | null): { color: string; bg: string; label: string } {
  if (p === null) return { color: '#6b7280', bg: '#f3f4f6', label: 'Sin datos' };
  if (p < 3) return { color: '#ef4444', bg: '#fef2f2', label: `<3 · Bajo` };
  if (p < 15) return { color: '#f59e0b', bg: '#fffbeb', label: `P${p.toFixed(0)} · Límite` };
  if (p < 85) return { color: '#10b981', bg: '#f0fdf4', label: `P${p.toFixed(0)} · Normal` };
  if (p < 97) return { color: '#f59e0b', bg: '#fffbeb', label: `P${p.toFixed(0)} · Elevado` };
  return { color: '#ef4444', bg: '#fef2f2', label: `>97 · Alto` };
}

const GrowthSection: React.FC<Props> = ({ growth, setPediatricField }) => {
  const set = (field: string, value: any) => setPediatricField('growth', field, value);

  const wp = getPercentileColor(growth.weight_percentile);
  const hp = getPercentileColor(growth.height_percentile);
  const hcp = getPercentileColor(growth.head_percentile);
  const bp = getPercentileColor(growth.bmi_percentile);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Crecimiento y Percentiles</h5>
        <div className="row mt-3">
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Peso Actual (kg)</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                min={0}
                value={growth.weight_kg ?? ''}
                onChange={(e) => set('weight_kg', e.target.value ? Number(e.target.value) : null)}
                placeholder="kg"
              />
            </div>
            {growth.weight_percentile !== null && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: wp.color,
                  background: wp.bg,
                  border: `1px solid ${wp.color}40`,
                }}
              >
                Peso: {wp.label}
              </div>
            )}
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Talla Actual (cm)</label>
              <input
                type="number"
                step="0.1"
                className="form-control"
                min={0}
                value={growth.height_cm ?? ''}
                onChange={(e) => set('height_cm', e.target.value ? Number(e.target.value) : null)}
                placeholder="cm"
              />
            </div>
            {growth.height_percentile !== null && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: hp.color,
                  background: hp.bg,
                  border: `1px solid ${hp.color}40`,
                }}
              >
                Talla: {hp.label}
              </div>
            )}
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">PC Actual (cm)</label>
              <input
                type="number"
                step="0.1"
                className="form-control"
                min={0}
                value={growth.head_cm ?? ''}
                onChange={(e) => set('head_cm', e.target.value ? Number(e.target.value) : null)}
                placeholder="cm"
              />
            </div>
            {growth.head_percentile !== null && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: hcp.color,
                  background: hcp.bg,
                  border: `1px solid ${hcp.color}40`,
                }}
              >
                PC: {hcp.label}
              </div>
            )}
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Método de Medición</label>
              <select
                className="form-control"
                value={growth.measurement_method}
                onChange={(e) => set('measurement_method', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="Acostado">Acostado</option>
                <option value="Parado">Parado</option>
              </select>
            </div>
          </div>
        </div>
        {growth.bmi !== null && (
          <div className="row mt-2">
            <div className="col-12">
              <div
                style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 14,
                  background: '#f0f9f0',
                  border: `1px solid ${bp.color}40`,
                }}
              >
                <strong>IMC: {growth.bmi.toFixed(1)}</strong>
                {growth.bmi_percentile !== null && (
                  <span style={{ color: bp.color, fontWeight: 600, marginLeft: 8 }}>
                    · {bp.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthSection;
